export type Werkzeug = "rahmen" | "pfeil" | "stift" | "text" | "schwaerzen";
export type Form =
  | { w: "rahmen" | "schwaerzen"; x: number; y: number; b: number; h: number }
  | { w: "pfeil"; x1: number; y1: number; x2: number; y2: number }
  | { w: "stift"; punkte: [number, number][] }
  | { w: "text"; x: number; y: number; text: string };

/** Einzige Markierfarbe (PO: „nur Rot, niemand soll überlegen müssen"). */
export const MARKIER_ROT = "#d4202a";

/** Schriftgröße des eingebrannten Texts in Bildpixeln — hängt wie die Linienstärke an der Bildbreite. */
export const textGroesse = (linie: number): number => linie * 6;
export const TEXT_FAMILIE = `"Hanken Grotesk", system-ui, sans-serif`;
/** Canvas-Font für Text; dieselbe Angabe nutzt das Eingabefeld im Editor (dort auf Anzeigegröße skaliert). */
export const textSchrift = (linie: number): string => `600 ${textGroesse(linie)}px ${TEXT_FAMILIE}`;

/** Misst einen Text in Bildpixeln (Breite b, Höhe h) — im Editor über ctx.measureText, im Test fest. */
export type TextMessen = (text: string) => { b: number; h: number };
type TextForm = Extract<Form, { w: "text" }>;

/** Rahmen eines Texts in Bildpixeln (Text wird mit textBaseline "top" ab x/y gezeichnet), optional mit Greifrand. */
export function textBox(f: TextForm, messen: TextMessen, rand = 0): { x: number; y: number; b: number; h: number } {
  const m = messen(f.text);
  return { x: f.x - rand, y: f.y - rand, b: m.b + 2 * rand, h: m.h + 2 * rand };
}

/** Index des obersten Texts unter (x, y) in Bildpixeln, sonst -1. Andere Formen zählen nicht. */
export function trefferText(formen: Form[], x: number, y: number, messen: TextMessen, rand = 0): number {
  for (let i = formen.length - 1; i >= 0; i--) {
    const f = formen[i];
    if (f.w !== "text") continue;
    const r = textBox(f, messen, rand);
    if (x >= r.x && x <= r.x + r.b && y >= r.y && y <= r.y + r.h) return i;
  }
  return -1;
}

/** Neue, um (dx, dy) verschobene Form; das Original bleibt unverändert. */
export function verschiebeForm(f: Form, dx: number, dy: number): Form {
  switch (f.w) {
    case "pfeil": return { ...f, x1: f.x1 + dx, y1: f.y1 + dy, x2: f.x2 + dx, y2: f.y2 + dy };
    case "stift": return { ...f, punkte: f.punkte.map(([px, py]) => [px + dx, py + dy] as [number, number]) };
    default: return { ...f, x: f.x + dx, y: f.y + dy };
  }
}

/**
 * Zustand des Markier-Editors: die fertigen Formen plus Verlauf als Schnappschüsse.
 * Jede wirksame Aktion legt genau EINEN Schnappschuss ab — Rückgängig nimmt ihn zurück.
 */
export type MarkierZustand = { formen: Form[]; verlauf: Form[][] };
export type MarkierAktion =
  | { typ: "hinzu"; form: Form }
  | { typ: "verschieben"; index: number; dx: number; dy: number }
  | { typ: "aendern"; index: number; text: string }
  | { typ: "rueckgaengig" };
export const MARKIER_START: MarkierZustand = { formen: [], verlauf: [] };

export function markierReducer(s: MarkierZustand, a: MarkierAktion): MarkierZustand {
  const schritt = (formen: Form[]): MarkierZustand => ({ formen, verlauf: [...s.verlauf, s.formen] });
  switch (a.typ) {
    case "hinzu":
      return schritt([...s.formen, a.form]);
    case "verschieben": {
      const f = s.formen[a.index];
      if (!f || (a.dx === 0 && a.dy === 0)) return s;
      return schritt(s.formen.map((g, i) => (i === a.index ? verschiebeForm(g, a.dx, a.dy) : g)));
    }
    case "aendern": {
      const f = s.formen[a.index];
      if (!f || f.w !== "text") return s;
      const text = a.text.trim();
      if (!text) return schritt(s.formen.filter((_, i) => i !== a.index));
      if (text === f.text) return s;
      return schritt(s.formen.map((g, i) => (i === a.index ? { ...f, text } : g)));
    }
    case "rueckgaengig": {
      if (!s.verlauf.length) return s;
      return { formen: s.verlauf[s.verlauf.length - 1], verlauf: s.verlauf.slice(0, -1) };
    }
  }
}

/** Pfeilspitze als zwei Punkte hinter der Spitze, symmetrisch zur Pfeilachse. */
export function pfeilspitze(x1: number, y1: number, x2: number, y2: number, laenge: number): [[number, number], [number, number]] {
  const winkel = Math.atan2(y2 - y1, x2 - x1);
  const off = Math.PI / 7;
  return [
    [x2 - laenge * Math.cos(winkel - off), y2 - laenge * Math.sin(winkel - off)],
    [x2 - laenge * Math.cos(winkel + off), y2 - laenge * Math.sin(winkel + off)],
  ];
}

/** Zeichnet alle Formen auf den Canvas-Kontext: Schwärzen deckend schwarz, alles andere in MARKIER_ROT. */
export function zeichneFormen(ctx: CanvasRenderingContext2D, formen: Form[], linie: number): void {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const f of formen) {
    if (f.w === "schwaerzen") {
      ctx.fillStyle = "#000";
      ctx.fillRect(f.x, f.y, f.b, f.h);
      continue;
    }
    ctx.strokeStyle = MARKIER_ROT;
    ctx.fillStyle = MARKIER_ROT;
    ctx.lineWidth = linie;
    if (f.w === "rahmen") {
      ctx.strokeRect(f.x, f.y, f.b, f.h);
    } else if (f.w === "pfeil") {
      ctx.beginPath();
      ctx.moveTo(f.x1, f.y1);
      ctx.lineTo(f.x2, f.y2);
      ctx.stroke();
      const [[ax, ay], [bx, by]] = pfeilspitze(f.x1, f.y1, f.x2, f.y2, linie * 5);
      ctx.beginPath();
      ctx.moveTo(f.x2, f.y2);
      ctx.lineTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.closePath();
      ctx.fill();
    } else if (f.w === "stift") {
      if (f.punkte.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(f.punkte[0][0], f.punkte[0][1]);
      for (const [x, y] of f.punkte.slice(1)) ctx.lineTo(x, y);
      ctx.stroke();
    } else if (f.w === "text") {
      ctx.font = textSchrift(linie);
      ctx.textBaseline = "top";
      ctx.fillText(f.text, f.x, f.y);
    }
  }
}
